import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import * as d3 from 'd3';
import {
  Loader2, Calendar, ChevronRight, Wrench, AlertTriangle,
  RefreshCw, CalendarClock, Circle, Sparkles,
} from 'lucide-react';
import { useAppSelector } from '../../redux/hooks';
import { useTheme } from '../../contexts';
import { useSharedAgentChat } from '../../contexts/AgentChatContext';
import {
  useListPortfolioSitesQuery,
  useListAdminSitesQuery,
  type PortfolioSite,
} from '../../redux/api/portfolioApi';
import { useGetSiteIntelligenceQuery, type EventSummary } from '../../redux/api/eventsApi';
import { useGetComponentListQuery } from '../../redux/api/entityApi';
import { ALL_EVENT_CATEGORIES, EVENT_CATEGORY_CONFIG, deriveEventCategory, type EventCategory } from '../../constants/eventCategories';
import { EVENT_CATEGORY_HEX_COLORS, EVENT_CATEGORY_LABELS, SEVERITY_HEX_COLORS } from '../../constants/eventStyles';
import EventDetailPanel from '../events/EventDetailPanel';

// --- Category icons ---
const CATEGORY_ICONS: Record<EventCategory, React.ComponentType<any>> = {
  preventative_maintenance: Wrench,
  corrective_maintenance: AlertTriangle,
  asset_update: RefreshCw,
  upcoming: CalendarClock,
  other: Circle,
};

// --- Gradient definitions for categories ---
const CATEGORY_GRADIENTS: Record<EventCategory, { from: string; to: string }> = {
  preventative_maintenance: { from: '#3B82F6', to: '#93C5FD' },
  corrective_maintenance: { from: '#EF4444', to: '#FCA5A5' },
  asset_update: { from: '#8B5CF6', to: '#C4B5FD' },
  upcoming: { from: '#F59E0B', to: '#FCD34D' },
  other: { from: '#6B7280', to: '#D1D5DB' },
};

/** Minimum horizontal pixels allocated per day in the timeline chart area. */
const TIMELINE_PX_PER_DAY = 6;

// --- Drill state ---
type DrillLevel = 'portfolio' | 'site' | 'component' | 'category';

interface BreadcrumbItem {
  level: DrillLevel;
  label: string;
  siteId?: string;
  componentType?: string;
  componentId?: string;
  category?: EventCategory;
}

interface DrillState {
  level: DrillLevel;
  siteId?: string;
  componentType?: string;
  componentId?: string;
  category?: EventCategory;
  breadcrumb: BreadcrumbItem[];
}

// --- Processed event with derived category ---
interface ProcessedEvent {
  id: string;
  title: string;
  event_type: string;
  severity: string;
  status: string;
  occurred_at: string;
  resolved_at: string | null;
  category: EventCategory;
  affected_entities: Array<{ entity_type: string; entity_identifier: string; role: string }>;
}

// --- Intelligence fetcher for a single site ---
const SiteIntelligenceRow: React.FC<{
  site: PortfolioSite;
  timeScale: d3.ScaleTime<number, number>;
  svgWidth: number;
  categoryFilters: Set<EventCategory>;
  onDrillToSite: (siteId: string, siteName: string) => void;
  isDark: boolean;
  rowHeight: number;
}> = ({ site, timeScale, svgWidth, categoryFilters, onDrillToSite, isDark, rowHeight }) => {
  const siteId = site.site_id || site.id || '';
  const siteName = site.site_name || site.name || 'Unknown';

  const { data: intelData, isLoading } = useGetSiteIntelligenceQuery(
    { siteId, eventsLimit: 100, tasksLimit: 0 },
    { skip: !siteId }
  );

  const events = useMemo(() => {
    if (!intelData?.events) return [];
    return intelData.events
      .map((e) => ({
        ...e,
        category: (e as any).event_category || deriveEventCategory(e.event_type, e.occurred_at),
      }))
      .filter((e) => categoryFilters.size === 0 || categoryFilters.has(e.category));
  }, [intelData, categoryFilters]);

  const labelWidth = 180;

  return (
    <g>
      {/* Row background */}
      <rect
        x={0} y={0}
        width={svgWidth}
        height={rowHeight}
        fill={isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.015)'}
        className="cursor-pointer hover:fill-[rgba(23,196,128,0.08)]"
        onClick={() => onDrillToSite(siteId, siteName)}
      />
      {/* Row label */}
      <text
        x={12} y={rowHeight / 2}
        dominantBaseline="middle"
        fill={isDark ? '#E5E7EB' : '#374151'}
        fontSize={12}
        fontWeight={600}
        className="cursor-pointer"
        onClick={() => onDrillToSite(siteId, siteName)}
      >
        {siteName.length > 22 ? siteName.substring(0, 22) + '...' : siteName}
      </text>
      {/* Drill arrow */}
      <text
        x={labelWidth - 20} y={rowHeight / 2}
        dominantBaseline="middle"
        fill={isDark ? '#6B7280' : '#9CA3AF'}
        fontSize={10}
        className="cursor-pointer"
        onClick={() => onDrillToSite(siteId, siteName)}
      >
        ▸
      </text>
      {/* Loading indicator */}
      {isLoading && (
        <text x={labelWidth + 10} y={rowHeight / 2} dominantBaseline="middle" fill="#9CA3AF" fontSize={10}>
          Loading...
        </text>
      )}
      {/* Event dots */}
      {events.map((event) => {
        const x = timeScale(new Date(event.occurred_at));
        if (x < labelWidth || x > svgWidth - 8) return null;
        const jitter = ((event.id.charCodeAt(0) % 5) - 2) * 3;
        const gradient = CATEGORY_GRADIENTS[event.category];
        return (
          <circle
            key={event.id}
            cx={x}
            cy={rowHeight / 2 + jitter}
            r={4}
            fill={gradient.from}
            opacity={0.8}
            className="cursor-pointer hover:opacity-100 transition-opacity"
          >
            <title>{`${event.title} (${event.severity} · ${event.event_type})`}</title>
          </circle>
        );
      })}
      {/* Event count badge */}
      {events.length > 0 && (
        <text
          x={svgWidth - 8}
          y={rowHeight / 2}
          dominantBaseline="middle"
          textAnchor="end"
          fill={isDark ? '#9CA3AF' : '#6B7280'}
          fontSize={10}
        >
          {events.length}
        </text>
      )}
    </g>
  );
};

// --- Main component ---
const InteractiveTimelineTab: React.FC = () => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const profile = useAppSelector((state) => state.auth.profile);
  const currentSite = useAppSelector((state) => state.site.currentSite);
  const selectedPortfolioId = useAppSelector((state) => state.portfolio.selectedPortfolioId);

  const isSuperAdmin = ((profile?.email || '') as string).toLowerCase().endsWith('@engramiq.com');
  const { sendMessage } = useSharedAgentChat();
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // Category filters
  const [categoryFilters, setCategoryFilters] = useState<Set<EventCategory>>(new Set());

  // Event detail sidebar
  const [selectedEvent, setSelectedEvent] = useState<{ siteId: string; eventId: string } | null>(null);

  const svgRef = useRef<SVGSVGElement>(null);

  // Drill-down state — start at site level if a site is selected
  const currentSiteId = currentSite?.site_id || currentSite?.id || '';
  const currentSiteName = currentSite?.site_name || currentSite?.name || 'Site';

  const [drill, setDrill] = useState<DrillState>(() => {
    if (currentSiteId) {
      return {
        level: 'site',
        siteId: currentSiteId,
        breadcrumb: [
          { level: 'portfolio', label: 'Portfolio' },
          { level: 'site', label: currentSiteName, siteId: currentSiteId },
        ],
      };
    }
    return {
      level: 'portfolio',
      breadcrumb: [{ level: 'portfolio', label: 'Portfolio' }],
    };
  });

  // Update drill state when site changes
  useEffect(() => {
    if (currentSiteId) {
      setDrill({
        level: 'site',
        siteId: currentSiteId,
        breadcrumb: [
          { level: 'portfolio', label: 'Portfolio' },
          { level: 'site', label: currentSiteName, siteId: currentSiteId },
        ],
      });
    }
  }, [currentSiteId, currentSiteName]);

  // --- Fetch portfolio sites ---
  const usePortfolioScoping = !!selectedPortfolioId;
  const { data: portfolioSitesData, isLoading: portfolioLoading } = useListPortfolioSitesQuery(
    { portfolio_id: selectedPortfolioId || '', limit: 100 },
    { skip: !selectedPortfolioId }
  );
  const { data: adminSitesData, isLoading: adminLoading } = useListAdminSitesQuery(
    { limit: 100 },
    { skip: !isSuperAdmin || usePortfolioScoping }
  );

  const sites: PortfolioSite[] = useMemo(() => {
    if (usePortfolioScoping) return portfolioSitesData?.items || [];
    if (isSuperAdmin) return adminSitesData?.items || [];
    return portfolioSitesData?.items || [];
  }, [usePortfolioScoping, isSuperAdmin, portfolioSitesData, adminSitesData]);

  const sitesLoading = usePortfolioScoping ? portfolioLoading : (isSuperAdmin ? adminLoading : portfolioLoading);

  // --- Fetch intelligence for drilled-into site ---
  const drillSiteId = drill.level !== 'portfolio' ? drill.siteId : undefined;
  const { data: siteIntelData, isLoading: siteIntelLoading } = useGetSiteIntelligenceQuery(
    { siteId: drillSiteId || '', eventsLimit: 100, tasksLimit: 0 },
    { skip: !drillSiteId }
  );

  // --- Fetch component list for drilled-into site ---
  const portfolioIdForComponents = useMemo(() => {
    if (drill.siteId) {
      const site = sites.find((s) => (s.site_id || s.id) === drill.siteId);
      return site?.portfolio_id || selectedPortfolioId || '';
    }
    return '';
  }, [drill.siteId, sites, selectedPortfolioId]);

  const { data: componentsData } = useGetComponentListQuery(
    { portfolio_id: portfolioIdForComponents, site_id: drillSiteId || '' },
    { skip: !portfolioIdForComponents || !drillSiteId }
  );

  // --- Processed events for drilled site ---
  const siteEvents: ProcessedEvent[] = useMemo(() => {
    if (!siteIntelData?.events) return [];
    return siteIntelData.events
      .map((e) => ({
        id: e.id,
        title: e.title,
        event_type: e.event_type,
        severity: e.severity,
        status: e.status,
        occurred_at: e.occurred_at,
        resolved_at: e.resolved_at,
        category: ((e as any).event_category || deriveEventCategory(e.event_type, e.occurred_at)) as EventCategory,
        affected_entities: e.affected_entities || [],
      }))
      .filter((e) => categoryFilters.size === 0 || categoryFilters.has(e.category));
  }, [siteIntelData, categoryFilters]);

  // --- Component grouping for site drill ---
  const componentGroups = useMemo(() => {
    if (drill.level === 'portfolio' || siteEvents.length === 0) return [];

    const components = componentsData?.items || [];
    const entityToComp = new Map<string, { type: string; id: string; name: string }>();
    components.forEach((c) => {
      entityToComp.set(c.primary_id, {
        type: c.component_type || 'Unknown',
        id: c.uuid,
        name: c.primary_id,
      });
    });

    // Group events by component type → component
    const typeMap = new Map<string, Map<string, ProcessedEvent[]>>();
    const siteLevel: ProcessedEvent[] = [];

    siteEvents.forEach((event) => {
      const entities = event.affected_entities;
      if (entities.length === 0) {
        siteLevel.push(event);
        return;
      }
      entities.forEach((ent) => {
        const comp = entityToComp.get(ent.entity_identifier);
        const compType = comp?.type || 'Unknown';
        const compName = ent.entity_identifier;
        if (!typeMap.has(compType)) typeMap.set(compType, new Map());
        const compMap = typeMap.get(compType)!;
        if (!compMap.has(compName)) compMap.set(compName, []);
        compMap.get(compName)!.push(event);
      });
    });

    const groups: Array<{
      type: string;
      components: Array<{ name: string; events: ProcessedEvent[] }>;
      totalEvents: number;
    }> = [];

    // Site-level events as a group
    if (siteLevel.length > 0) {
      groups.push({
        type: 'Site-Level',
        components: [{ name: 'Site Events', events: siteLevel }],
        totalEvents: siteLevel.length,
      });
    }

    // Component type groups
    Array.from(typeMap.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .forEach(([typeName, compMap]) => {
        const comps = Array.from(compMap.entries())
          .sort((a, b) => a[0].localeCompare(b[0]))
          .map(([name, events]) => ({ name, events }));
        groups.push({
          type: typeName,
          components: comps,
          totalEvents: comps.reduce((sum, c) => sum + c.events.length, 0),
        });
      });

    return groups;
  }, [siteEvents, componentsData, drill.level]);

  // --- Compute global time range across all visible data ---
  // No future space reserved — range extends to the latest event (or now)
  const globalDateRange = useMemo((): [Date, Date] => {
    const now = new Date();
    const yearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);

    if (drill.level !== 'portfolio' && siteEvents.length > 0) {
      const dates = siteEvents.map((e) => new Date(e.occurred_at).getTime());
      const min = Math.min(...dates);
      const max = Math.max(...dates, now.getTime());
      const range = max - min || 86400000;
      return [new Date(min - range * 0.03), new Date(max + range * 0.02)];
    }

    // Portfolio level: span from 2 years ago to now
    const twoYearsAgo = new Date(now.getTime() - 2 * 365 * 24 * 60 * 60 * 1000);
    return [twoYearsAgo, now];
  }, [drill.level, siteEvents]);

  // --- Time scale ---
  const labelWidth = 180;
  const timelineWidth = useMemo(() => {
    const [start, end] = globalDateRange;
    const days = Math.max(1, (end.getTime() - start.getTime()) / 86_400_000);
    const chartWidth = days * TIMELINE_PX_PER_DAY;
    return Math.max(dimensions.width, labelWidth + chartWidth + 32);
  }, [globalDateRange, dimensions.width]);

  const timeScale = useMemo(() => {
    return d3.scaleTime()
      .domain(globalDateRange)
      .range([labelWidth, timelineWidth - 8]);
  }, [globalDateRange, timelineWidth]);

  // --- Resize observer ---
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setDimensions({
          width: entry.contentRect.width,
          height: entry.contentRect.height,
        });
      }
    });
    observer.observe(el);
    setDimensions({
      width: el.getBoundingClientRect().width,
      height: el.getBoundingClientRect().height,
    });
    return () => observer.disconnect();
  }, []);

  // Wheel over the chart area pans horizontally; wheel over labels scrolls vertically.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onWheel = (e: WheelEvent) => {
      if (el.scrollWidth <= el.clientWidth) return;
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;

      const rect = el.getBoundingClientRect();
      const overChart = e.clientX - rect.left >= labelWidth;
      if (!overChart || e.deltaY === 0) return;

      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [timelineWidth, labelWidth]);

  // --- Category filter toggle ---
  const toggleCategory = useCallback((cat: EventCategory) => {
    setCategoryFilters((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);
      else next.add(cat);
      return next;
    });
  }, []);

  // --- AI Summarize ---
  const handleSummarize = useCallback((label: string, events: ProcessedEvent[]) => {
    const eventLines = events
      .sort((a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime())
      .map((e) => `- [${e.severity.toUpperCase()}] ${e.title} (${e.event_type}, ${e.status}, ${e.occurred_at.split('T')[0]})`)
      .join('\n');

    const prompt = `Summarize these ${events.length} events for "${label}". Be concise. Use your tools to research event details and affected component details before summarizing. Focus on: 1) Safety issues first, 2) High priority/critical items, 3) Trends and patterns.\n\nEvents:\n${eventLines}`;

    window.dispatchEvent(new CustomEvent('openAIChat'));
    // Small delay to let the chat panel open before sending
    setTimeout(() => sendMessage(prompt), 300);
  }, [sendMessage]);

  // --- Drill navigation ---
  const drillToSite = useCallback((siteId: string, siteName: string) => {
    setDrill({
      level: 'site',
      siteId,
      breadcrumb: [
        { level: 'portfolio', label: 'Portfolio' },
        { level: 'site', label: siteName, siteId },
      ],
    });
  }, []);

  const drillToComponent = useCallback((componentType: string, componentId: string, componentName: string) => {
    setDrill((prev) => ({
      level: 'component',
      siteId: prev.siteId,
      componentType,
      componentId,
      breadcrumb: [
        ...prev.breadcrumb.slice(0, 2),
        { level: 'component', label: componentName, componentType, componentId },
      ],
    }));
  }, []);

  const drillToCategory = useCallback((category: EventCategory) => {
    setDrill((prev) => ({
      level: 'category',
      siteId: prev.siteId,
      componentType: prev.componentType,
      componentId: prev.componentId,
      category,
      breadcrumb: [
        ...prev.breadcrumb,
        { level: 'category', label: EVENT_CATEGORY_LABELS[category], category },
      ],
    }));
  }, []);

  const navigateToBreadcrumb = useCallback((index: number) => {
    const bc = drill.breadcrumb.slice(0, index + 1);
    const target = bc[bc.length - 1];
    setDrill({
      level: target.level,
      siteId: target.siteId || drill.breadcrumb.find((b) => b.siteId)?.siteId,
      componentType: target.componentType,
      componentId: target.componentId,
      category: target.category,
      breadcrumb: bc,
    });
  }, [drill]);

  // --- Row height ---
  const rowHeight = 40;

  // --- Compute rows for current drill level ---
  const renderTimelineContent = () => {
    if (drill.level === 'portfolio') return renderPortfolioLevel();
    if (drill.level === 'site') return renderSiteLevel();
    if (drill.level === 'component') return renderComponentLevel();
    if (drill.level === 'category') return renderCategoryLevel();
    return null;
  };

  // --- Portfolio level: one row per site ---
  const renderPortfolioLevel = () => {
    if (sitesLoading) return null;
    return sites.map((site, i) => (
      <g key={site.site_id || site.id} transform={`translate(0, ${i * rowHeight})`}>
        <SiteIntelligenceRow
          site={site}
          timeScale={timeScale}
          svgWidth={timelineWidth}
          categoryFilters={categoryFilters}
          onDrillToSite={drillToSite}
          isDark={isDark}
          rowHeight={rowHeight}
        />
        <line
          x1={0} y1={rowHeight}
          x2={timelineWidth} y2={rowHeight}
          stroke={isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}
        />
      </g>
    ));
  };

  // --- Site level: rows grouped by component type ---
  const renderSiteLevel = () => {
    if (siteIntelLoading) return null;
    let yOffset = 0;
    const elements: React.ReactNode[] = [];

    componentGroups.forEach((group) => {
      // Group header row
      elements.push(
        <g key={`header-${group.type}`} transform={`translate(0, ${yOffset})`}>
          <rect
            x={0} y={0}
            width={timelineWidth}
            height={rowHeight}
            fill={isDark ? 'rgba(23,196,128,0.08)' : 'rgba(23,196,128,0.05)'}
          />
          <text
            x={12} y={rowHeight / 2}
            dominantBaseline="middle"
            fill="#17c480"
            fontSize={12}
            fontWeight={700}
          >
            {group.type}
          </text>
          <text
            x={labelWidth - 20} y={rowHeight / 2}
            dominantBaseline="middle"
            fill={isDark ? '#6B7280' : '#9CA3AF'}
            fontSize={10}
          >
            {group.totalEvents}
          </text>
          {/* AI Summarize button */}
          <foreignObject x={labelWidth + 4} y={(rowHeight - 20) / 2} width={20} height={20}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                const allEvents = group.components.flatMap((c) => c.events);
                handleSummarize(group.type, allEvents);
              }}
              className="flex items-center justify-center rounded transition-colors"
              style={{
                width: 20, height: 20,
                color: '#17c480',
                opacity: 0.6,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.opacity = '1';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.opacity = '0.6';
              }}
              title="AI Summarize"
            >
              <Sparkles style={{ width: 12, height: 12 }} />
            </button>
          </foreignObject>
          <line
            x1={0} y1={rowHeight}
            x2={timelineWidth} y2={rowHeight}
            stroke={isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}
          />
        </g>
      );
      yOffset += rowHeight;

      // Component rows within group
      group.components.forEach((comp) => {
        elements.push(
          <g key={`comp-${group.type}-${comp.name}`} transform={`translate(0, ${yOffset})`}>
            <rect
              x={0} y={0}
              width={timelineWidth}
              height={rowHeight}
              fill="transparent"
              className="cursor-pointer hover:fill-[rgba(23,196,128,0.05)]"
              onClick={() => drillToComponent(group.type, comp.name, comp.name)}
            />
            <text
              x={28} y={rowHeight / 2}
              dominantBaseline="middle"
              fill={isDark ? '#D1D5DB' : '#4B5563'}
              fontSize={11}
              fontWeight={500}
              className="cursor-pointer"
              onClick={() => drillToComponent(group.type, comp.name, comp.name)}
            >
              {comp.name.length > 20 ? comp.name.substring(0, 20) + '...' : comp.name}
            </text>
            <text
              x={labelWidth - 20} y={rowHeight / 2}
              dominantBaseline="middle"
              fill={isDark ? '#6B7280' : '#9CA3AF'}
              fontSize={10}
              className="cursor-pointer"
              onClick={() => drillToComponent(group.type, comp.name, comp.name)}
            >
              ▸
            </text>
            {/* Event dots for this component */}
            {comp.events.map((event) => {
              const x = timeScale(new Date(event.occurred_at));
              if (x < labelWidth || x > timelineWidth - 8) return null;
              const jitter = ((event.id.charCodeAt(0) % 5) - 2) * 2;
              const grad = CATEGORY_GRADIENTS[event.category];
              return (
                <circle
                  key={event.id}
                  cx={x}
                  cy={rowHeight / 2 + jitter}
                  r={4}
                  fill={grad.from}
                  opacity={0.8}
                  className="cursor-pointer hover:opacity-100"
                  onClick={() => setSelectedEvent({ siteId: drill.siteId!, eventId: event.id })}
                >
                  <title>{`${event.title} (${event.severity})`}</title>
                </circle>
              );
            })}
            <line
              x1={0} y1={rowHeight}
              x2={timelineWidth} y2={rowHeight}
              stroke={isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}
            />
          </g>
        );
        yOffset += rowHeight;
      });
    });

    return elements;
  };

  // --- Component level: events broken out by category ---
  const renderComponentLevel = () => {
    if (!drill.componentId) return null;

    const compEvents = siteEvents.filter((e) =>
      e.affected_entities.some((ent) => ent.entity_identifier === drill.componentId) ||
      (drill.componentId === 'Site Events' && e.affected_entities.length === 0)
    );

    const categoryMap = new Map<EventCategory, ProcessedEvent[]>();
    compEvents.forEach((event) => {
      if (!categoryMap.has(event.category)) categoryMap.set(event.category, []);
      categoryMap.get(event.category)!.push(event);
    });

    let yOffset = 0;
    const elements: React.ReactNode[] = [];

    ALL_EVENT_CATEGORIES.forEach((cat) => {
      const events = categoryMap.get(cat);
      if (!events || events.length === 0) return;

      const grad = CATEGORY_GRADIENTS[cat];

      elements.push(
        <g key={`cat-header-${cat}`} transform={`translate(0, ${yOffset})`}>
          <defs>
            <linearGradient id={`grad-${cat}`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor={grad.from} stopOpacity={isDark ? 0.15 : 0.1} />
              <stop offset="100%" stopColor={grad.to} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <rect
            x={0} y={0}
            width={timelineWidth}
            height={rowHeight}
            fill={`url(#grad-${cat})`}
            className="cursor-pointer"
            onClick={() => drillToCategory(cat)}
          />
          <circle cx={16} cy={rowHeight / 2} r={5} fill={grad.from} opacity={0.8} />
          <text
            x={28} y={rowHeight / 2}
            dominantBaseline="middle"
            fill={grad.from}
            fontSize={12}
            fontWeight={700}
            className="cursor-pointer"
            onClick={() => drillToCategory(cat)}
          >
            {EVENT_CATEGORY_CONFIG[cat].label}
          </text>
          <text
            x={labelWidth - 20} y={rowHeight / 2}
            dominantBaseline="middle"
            fill={isDark ? '#6B7280' : '#9CA3AF'}
            fontSize={10}
          >
            {events.length}
          </text>
          {/* AI Summarize button */}
          <foreignObject x={labelWidth + 4} y={(rowHeight - 20) / 2} width={20} height={20}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleSummarize(`${drill.componentId} — ${EVENT_CATEGORY_CONFIG[cat].label}`, events);
              }}
              className="flex items-center justify-center rounded transition-colors"
              style={{
                width: 20, height: 20,
                color: '#17c480',
                opacity: 0.6,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.opacity = '1';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.opacity = '0.6';
              }}
              title="AI Summarize"
            >
              <Sparkles style={{ width: 12, height: 12 }} />
            </button>
          </foreignObject>
          <line
            x1={0} y1={rowHeight}
            x2={timelineWidth} y2={rowHeight}
            stroke={isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'}
          />
        </g>
      );
      yOffset += rowHeight;

      events
        .sort((a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime())
        .forEach((event) => {
          const x = timeScale(new Date(event.occurred_at));
          const sevColor = SEVERITY_HEX_COLORS[event.severity as keyof typeof SEVERITY_HEX_COLORS] || '#6B7280';
          elements.push(
            <g key={`evt-${event.id}`} transform={`translate(0, ${yOffset})`}>
              <rect
                x={0} y={0}
                width={timelineWidth}
                height={rowHeight}
                fill="transparent"
                className="cursor-pointer hover:fill-[rgba(23,196,128,0.05)]"
                onClick={() => setSelectedEvent({ siteId: drill.siteId!, eventId: event.id })}
              />
              <text
                x={28} y={rowHeight / 2}
                dominantBaseline="middle"
                fill={isDark ? '#D1D5DB' : '#4B5563'}
                fontSize={11}
                className="cursor-pointer"
                onClick={() => setSelectedEvent({ siteId: drill.siteId!, eventId: event.id })}
              >
                {event.title.length > 22 ? event.title.substring(0, 22) + '...' : event.title}
              </text>
              <circle cx={labelWidth - 24} cy={rowHeight / 2} r={3} fill={sevColor} />
              {x >= labelWidth && x <= timelineWidth - 8 && (
                <>
                  <circle
                    cx={x} cy={rowHeight / 2} r={5}
                    fill={grad.from} stroke={grad.to} strokeWidth={1.5}
                    opacity={0.9}
                    className="cursor-pointer hover:opacity-100"
                    onClick={() => setSelectedEvent({ siteId: drill.siteId!, eventId: event.id })}
                  />
                  <text
                    x={x + 10} y={rowHeight / 2}
                    dominantBaseline="middle"
                    fill={isDark ? '#9CA3AF' : '#6B7280'}
                    fontSize={9}
                  >
                    {d3.timeFormat('%b %d, %Y')(new Date(event.occurred_at))}
                  </text>
                </>
              )}
              <line
                x1={0} y1={rowHeight}
                x2={timelineWidth} y2={rowHeight}
                stroke={isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)'}
              />
            </g>
          );
          yOffset += rowHeight;
        });
    });

    return elements;
  };

  // --- Category level: individual events of one category ---
  const renderCategoryLevel = () => {
    if (!drill.componentId || !drill.category) return null;

    const compEvents = siteEvents.filter((e) =>
      (e.affected_entities.some((ent) => ent.entity_identifier === drill.componentId) ||
      (drill.componentId === 'Site Events' && e.affected_entities.length === 0)) &&
      e.category === drill.category
    );

    const grad = CATEGORY_GRADIENTS[drill.category];
    let yOffset = 0;
    const elements: React.ReactNode[] = [];

    compEvents
      .sort((a, b) => new Date(b.occurred_at).getTime() - new Date(a.occurred_at).getTime())
      .forEach((event) => {
        const x = timeScale(new Date(event.occurred_at));
        const sevColor = SEVERITY_HEX_COLORS[event.severity as keyof typeof SEVERITY_HEX_COLORS] || '#6B7280';
        elements.push(
          <g key={`evt-${event.id}`} transform={`translate(0, ${yOffset})`}>
            <rect
              x={0} y={0}
              width={timelineWidth}
              height={rowHeight}
              fill="transparent"
              className="cursor-pointer hover:fill-[rgba(23,196,128,0.05)]"
              onClick={() => setSelectedEvent({ siteId: drill.siteId!, eventId: event.id })}
            />
            <rect x={4} y={4} width={3} height={rowHeight - 8} rx={1.5} fill={sevColor} />
            <text
              x={16} y={rowHeight / 2 - 6}
              dominantBaseline="middle"
              fill={isDark ? '#E5E7EB' : '#374151'}
              fontSize={11}
              fontWeight={600}
              className="cursor-pointer"
              onClick={() => setSelectedEvent({ siteId: drill.siteId!, eventId: event.id })}
            >
              {event.title.length > 30 ? event.title.substring(0, 30) + '...' : event.title}
            </text>
            <text
              x={16} y={rowHeight / 2 + 8}
              dominantBaseline="middle"
              fill={isDark ? '#9CA3AF' : '#6B7280'}
              fontSize={9}
            >
              {event.severity} · {event.event_type} · {d3.timeFormat('%b %d, %Y')(new Date(event.occurred_at))}
            </text>
            {x >= labelWidth && x <= timelineWidth - 8 && (
              <circle
                cx={x} cy={rowHeight / 2} r={6}
                fill={grad.from} stroke={grad.to} strokeWidth={2}
                opacity={0.9}
                className="cursor-pointer"
                onClick={() => setSelectedEvent({ siteId: drill.siteId!, eventId: event.id })}
              />
            )}
            <line
              x1={0} y1={rowHeight}
              x2={timelineWidth} y2={rowHeight}
              stroke={isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}
            />
          </g>
        );
        yOffset += rowHeight;
      });

    return elements;
  };

  // --- Compute total content height ---
  const contentHeight = useMemo(() => {
    if (drill.level === 'portfolio') return sites.length * rowHeight;
    if (drill.level === 'site') {
      let count = 0;
      componentGroups.forEach((g) => {
        count += 1;
        count += g.components.length;
      });
      return count * rowHeight;
    }
    if (drill.level === 'component') {
      let count = 0;
      const compEvents = siteEvents.filter((e) =>
        e.affected_entities.some((ent) => ent.entity_identifier === drill.componentId) ||
        (drill.componentId === 'Site Events' && e.affected_entities.length === 0)
      );
      const cats = new Set(compEvents.map((e) => e.category));
      cats.forEach((cat) => {
        count += 1;
        count += compEvents.filter((e) => e.category === cat).length;
      });
      return count * rowHeight;
    }
    if (drill.level === 'category') {
      const compEvents = siteEvents.filter((e) =>
        (e.affected_entities.some((ent) => ent.entity_identifier === drill.componentId) ||
        (drill.componentId === 'Site Events' && e.affected_entities.length === 0)) &&
        e.category === drill.category
      );
      return compEvents.length * rowHeight;
    }
    return 400;
  }, [drill, sites, componentGroups, siteEvents]);

  // --- X-axis ticks ---
  const axisTicks = useMemo(() => {
    const ticks = timeScale.ticks(8);
    return ticks.map((t) => ({
      x: timeScale(t),
      label: d3.timeFormat('%b %d, %Y')(t),
    }));
  }, [timeScale]);

  // --- Loading state ---
  const isLoading = sitesLoading || (drill.level !== 'portfolio' && siteIntelLoading);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full gap-2 text-[var(--color-text-muted)]">
        <Loader2 className="w-5 h-5 animate-spin" />
        Loading timeline...
      </div>
    );
  }

  if (drill.level === 'portfolio' && sites.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-[var(--color-text-muted)]">
        <Calendar className="w-10 h-10 mb-3 opacity-40" />
        <p className="text-sm font-medium">No sites available</p>
        <p className="text-xs mt-1">Add sites to your portfolio to see the timeline</p>
      </div>
    );
  }

  const axisHeight = 24;
  const svgHeight = Math.max(contentHeight + axisHeight, dimensions.height - axisHeight);

  return (
    <div className="h-full flex flex-col bg-[var(--color-bg-secondary)]">
      {/* Controls bar */}
      <div className="shrink-0 border-b border-[var(--color-border)] bg-[var(--color-surface)]">
        {/* Breadcrumb */}
        <div className="px-4 pt-2.5 pb-1 flex items-center gap-1 text-xs">
          {drill.breadcrumb.map((bc, i) => (
            <React.Fragment key={i}>
              {i > 0 && <ChevronRight className="w-3 h-3 text-[var(--color-text-muted)]" />}
              <button
                onClick={() => navigateToBreadcrumb(i)}
                className={`px-1.5 py-0.5 rounded transition-colors ${
                  i === drill.breadcrumb.length - 1
                    ? 'text-[#17c480] font-semibold'
                    : 'text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-surface-hover)]'
                }`}
              >
                {bc.label}
              </button>
            </React.Fragment>
          ))}
        </div>

        {/* Filters row */}
        <div className="px-4 py-2 flex items-center flex-wrap gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            {ALL_EVENT_CATEGORIES.map((cat) => {
              const Icon = CATEGORY_ICONS[cat];
              const isActive = categoryFilters.size === 0 || categoryFilters.has(cat);
              const grad = CATEGORY_GRADIENTS[cat];
              return (
                <button
                  key={cat}
                  onClick={() => toggleCategory(cat)}
                  className={`flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-medium transition-colors border border-transparent ${
                    isActive ? '' : 'opacity-40'
                  }`}
                  style={{
                    backgroundColor: isActive ? `${grad.from}15` : 'transparent',
                    color: grad.from,
                  }}
                  title={EVENT_CATEGORY_CONFIG[cat].label}
                >
                  <Icon className="w-3 h-3" />
                  {EVENT_CATEGORY_LABELS[cat]}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* Timeline */}
        <div ref={containerRef} className="flex-1 min-w-0 overflow-auto relative">
          {/* Sticky top axis — scrolls horizontally with content, stays pinned to top */}
          <div
            className="sticky top-0 z-10 pointer-events-none"
            style={{
              height: axisHeight,
              background: isDark ? '#0F172A' : '#FAFBFC',
              borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
            }}
          >
            <svg width={timelineWidth} height={axisHeight} className="block">
              {axisTicks.map((tick, i) => (
                <g key={i}>
                  <line
                    x1={tick.x} y1={axisHeight - 4}
                    x2={tick.x} y2={axisHeight}
                    stroke={isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'}
                  />
                  <text
                    x={tick.x} y={axisHeight - 8}
                    textAnchor="middle"
                    fill={isDark ? '#9CA3AF' : '#6B7280'}
                    fontSize={10}
                    fontWeight={500}
                  >
                    {tick.label}
                  </text>
                </g>
              ))}
              {/* Label area bg */}
              <rect x={0} y={0} width={labelWidth} height={axisHeight} fill={isDark ? '#0F172A' : '#FAFBFC'} />
            </svg>
          </div>

          <svg
            ref={svgRef}
            width={timelineWidth}
            height={svgHeight}
            className="block"
            style={{ background: isDark ? '#0F172A' : '#FAFBFC' }}
          >
            <defs>
              {ALL_EVENT_CATEGORIES.map((cat) => {
                const grad = CATEGORY_GRADIENTS[cat];
                return (
                  <linearGradient key={cat} id={`cat-grad-${cat}`} x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor={grad.from} stopOpacity={isDark ? 0.15 : 0.1} />
                    <stop offset="100%" stopColor={grad.to} stopOpacity={0.02} />
                  </linearGradient>
                );
              })}
            </defs>

            {/* Label column separator */}
            <line
              x1={labelWidth}
              y1={0}
              x2={labelWidth}
              y2={svgHeight}
              stroke={isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}
            />

            {/* X-axis vertical grid lines */}
            {axisTicks.map((tick, i) => (
              <line
                key={i}
                x1={tick.x} y1={0}
                x2={tick.x} y2={svgHeight}
                stroke={isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'}
              />
            ))}

            {/* Content rows */}
            {renderTimelineContent()}

            {/* Bottom axis labels */}
            {axisTicks.map((tick, i) => (
              <g key={`bottom-${i}`}>
                <line
                  x1={tick.x} y1={svgHeight - axisHeight}
                  x2={tick.x} y2={svgHeight - axisHeight + 4}
                  stroke={isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'}
                />
                <text
                  x={tick.x} y={svgHeight - 6}
                  textAnchor="middle"
                  fill={isDark ? '#9CA3AF' : '#6B7280'}
                  fontSize={10}
                  fontWeight={500}
                >
                  {tick.label}
                </text>
              </g>
            ))}
          </svg>
        </div>

        {/* Event detail sidebar */}
        {selectedEvent && (
          <EventDetailPanel
            siteId={selectedEvent.siteId}
            eventId={selectedEvent.eventId}
            onClose={() => setSelectedEvent(null)}
          />
        )}
      </div>
    </div>
  );
};

export default InteractiveTimelineTab;
